import { useTruncate } from '@hooks/useTruncate';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import { Card, CircularProgress, IconButton } from '@mui/material';
import Button from '@mui/material/Button';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMultipartUpload } from 'services/file-manager/file-managerHook';
import { useUpdateUser, useUsersStore } from 'services/users';
import farmerImage from '../../../../assets/farmer.jpg';
import './styles.css';

interface UserCardProps {
	name: string;
	onEdit?: () => void;
	children?: React.ReactNode;
	readonly?: boolean;
}

export const FarmerUserCard = ({ name, onEdit, children, readonly = false }: UserCardProps) => {
	const { t } = useTranslation();
	const { user } = useUsersStore();
	const { uploadFiles, resetStatus, isLoading } = useMultipartUpload();
	const mutate = useUpdateUser();
	const { truncate } = useTruncate();

	const handleFileChange = useCallback(
		async (event: React.ChangeEvent<HTMLInputElement>) => {
			const files = Array.from(event.target.files || []);

			if (files.length > 0) {
				const uploadResults = await uploadFiles(files);

				if (uploadResults) {
					await mutate({
						profile_image: uploadResults[0]?.key,
					});
				}
				resetStatus();
				event.target.value = '';
			}
		},
		[mutate, resetStatus, uploadFiles]
	);

	return (
		<>
			<Card
				sx={{
					borderRadius: '15px',
					boxShadow: '0px 2px 6px 0px #00000040',
					backgroundColor: 'white',
					padding: '20px',
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
				}}
				className="responsive-farmer-card flex-col"
			>
				<CardContent
					sx={{ display: 'flex', gap: '20px', alignItems: 'center' }}
					className="flex-col"
				>
					<div className="relative">
						{!readonly && (
							<>
								{isLoading ? (
									<CircularProgress />
								) : (
									<>
										<CardMedia
											sx={{
												width: '103px',
												height: '102px',
												borderRadius: '8px',
												boxShadow: '0px 2px 6px 0px #00000040',
											}}
											component="img"
											image={
												user?.profile_image
													? `${process.env.REACT_APP_CDN_URL}${user?.profile_image}`
													: farmerImage
											}
											alt="user"
										/>
										<input
											type="file"
											accept="image/png, image/jpeg"
											style={{ display: 'none' }}
											id="upload-photo"
											onChange={handleFileChange}
										/>
										<label htmlFor="upload-photo" className="cursor-pointer">
											<IconButton
												sx={{
													borderRadius: '8px',
													width: '24px',
													height: '24px',
													boxShadow: '0px 2px 6px 0px #00000040',
													position: 'absolute',
													padding: '5px',
													background: '#fff',
													cursor: 'pointer',
													'&:hover': {
														backgroundColor: '#fff',
														cursor: 'pointer',
													},
												}}
												className="icon-position-responsive"
												component="span"
											>
												<CreateOutlinedIcon
													sx={{ color: '#00AB89', width: '14px', height: '14px' }}
												/>
											</IconButton>
										</label>
									</>
								)}
							</>
						)}
					</div>
					<div className="flex justify-center items-center gap-[10px]">
						<Typography
							gutterBottom
							sx={{
								color: '#343A40',
								font: 'roboto-bold',
								fontSize: '20px !important',
								lineHeight: '140%',
								marginBottom: '0',
							}}
							component="div"
						>
							{truncate(name, 25)}
						</Typography>
						{!readonly && (
							<Button
								sx={{
									borderRadius: '8px',
									background: '#F8F9FA',
									height: '34px',
									padding: '5px 10px',
									alignItems: 'center',
									color: '#00AB89',
									fontFamily: 'Roboto',
									fontSize: '16px',
									fontStyle: 'normal',
									fontWeight: 600,
									lineHeight: '140%',
									boxShadow: 'unset',
									textTransform: 'capitalize',
								}}
								variant="contained"
								startIcon={<CreateOutlinedIcon />}
								onClick={onEdit}
							>
								{t('editable')}
							</Button>
						)}
					</div>
				</CardContent>
				{children}
			</Card>
		</>
	);
};
